import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { MapPin, X, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  '75', '77', '78', '91', '92', '93', '94', '95',
  'Paris', 'Yvelines', 'Île-de-France',
  '69', 'Rhône', 'Lyon', 'Auvergne-Rhône-Alpes',
  '13', 'Marseille', 'PACA',
  '33', 'Bordeaux', 'Nouvelle-Aquitaine',
  '59', 'Lille', 'Hauts-de-France',
];

/** Dialog to edit a client's target geographic zones used for SEO opportunity scoring. */
export default function ZonesCiblesDialog({ open, onOpenChange, client, onSaved }) {
  const [zones, setZones] = useState([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) setZones(client.target_zones || []);
  }, [client]);

  const addZone = (z) => {
    const trimmed = (z || '').trim();
    if (!trimmed || zones.includes(trimmed)) return;
    if (zones.length >= 20) {
      toast.error('Maximum 20 zones par client');
      return;
    }
    setZones([...zones, trimmed]);
    setInput('');
  };

  const removeZone = (z) => setZones(zones.filter(x => x !== z));

  const handleSave = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/clients/${client.id}/stats-settings`,
        { target_zones: zones },
        { withCredentials: true }
      );
      toast.success(`${client.name} : ${zones.length} zone${zones.length > 1 ? 's' : ''} cible${zones.length > 1 ? 's' : ''} enregistrée${zones.length > 1 ? 's' : ''}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addZone(input);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#002FA7]" />
            Zones cibles — {client?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">À quoi ça sert ?</p>
            <p>Les opportunités de mots-clés seront <strong>réorientées vers le territoire de ce client</strong> :</p>
            <ul className="text-xs mt-2 space-y-0.5 ml-4 list-disc">
              <li>×5 sur le score si le mot-clé cible exactement une zone (ex : "78", "Versailles")</li>
              <li>×2.5 si le mot-clé cible une région du client</li>
              <li>×0.1 si le mot-clé cible une autre zone (cachable via le filtre)</li>
              <li>×1 sur les mots-clés nationaux</li>
            </ul>
          </div>

          <div>
            <Label className="text-sm">Ajouter une zone (département, ville ou région)</Label>
            <div className="flex gap-2 mt-1">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ex : 78, Yvelines, Versailles, Île-de-France"
                className="flex-1 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="zone-input"
              />
              <Button onClick={() => addZone(input)} variant="outline" size="sm" disabled={!input.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {zones.length === 0 ? (
              <span className="text-xs text-neutral-400 italic">Aucune zone — les mots-clés seront tous traités comme nationaux.</span>
            ) : (
              zones.map(z => (
                <span key={z} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {z}
                  <button onClick={() => removeZone(z)} className="hover:text-red-600" data-testid={`remove-zone-${z}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))
            )}
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Suggestions courantes</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {SUGGESTIONS.filter(s => !zones.includes(s)).slice(0, 12).map(s => (
                <button
                  key={s}
                  onClick={() => addZone(s)}
                  className="text-xs px-2 py-0.5 rounded bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
                  data-testid={`suggestion-${s}`}
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Annuler
            </Button>
            <Button className="bg-[#002FA7] hover:bg-[#002277]" onClick={handleSave} disabled={saving} data-testid="save-zones-btn">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
